/**
 * Wire the reference-video element into a WebAudio graph so the RefVoiceFader
 * actually attenuates playback level (vs. being decorative state).
 *
 *   <video ref={videoRef} muted ... />
 *           │
 *           ▼ MediaElementAudioSource
 *           ▼ GainNode (driven by refVoiceVolumeDb / refVoiceMuted store)
 *           ▼ AudioContext.destination
 *
 * Why route through WebAudio when we could just set `video.volume`?
 *  • dB scaling. Native `<video>.volume` is a 0..1 linear ramp; users hear
 *    -6 dB ≈ "half as loud", which the linear curve doesn't match.
 *  • Auto-mute on record. We need the reference voice silenced while the
 *    actor performs (so the take doesn't bleed reference audio). Toggling
 *    `<video>.muted` would also kill the playback time-update events some
 *    UI widgets rely on, so muting the gain node is safer.
 *
 * The video element itself stays `muted` from a DOM perspective so autoplay
 * policies don't gate playback — WebAudio gets the audio via
 * `createMediaElementSource` and routes it to `destination` on its own.
 *
 * Returns nothing — the hook just plumbs the graph into existence.
 */
import type { RefObject } from 'react'
import { useEffect, useRef } from 'react'
import { useRecordingStore } from './state'

export interface UseRefVoiceGainOptions {
  videoRef: RefObject<HTMLVideoElement | null>
  /**
   * When true, gain is forced to 0 regardless of fader/mute state. Used while
   * the actor is in the three_pop / recording phase so the reference doesn't
   * bleed into the take.
   */
  muteForRecord: boolean
}

/** Convert decibels to a linear gain coefficient (10^(dB/20)). */
export function dbToGain(db: number): number {
  return 10 ** (db / 20)
}

export function useRefVoiceGain({ videoRef, muteForRecord }: UseRefVoiceGainOptions): void {
  const volumeDb = useRecordingStore((s) => s.refVoiceVolumeDb)
  const muted = useRecordingStore((s) => s.refVoiceMuted)

  const ctxRef = useRef<AudioContext | null>(null)
  const sourceRef = useRef<MediaElementAudioSourceNode | null>(null)
  const gainRef = useRef<GainNode | null>(null)

  // Lazy graph construction: createMediaElementSource throws if the same
  // element gets wired twice, and AudioContext should only be created once
  // per video lifetime. We attach when the <video> element mounts and tear
  // down on unmount.
  useEffect(() => {
    const v = videoRef.current
    if (!v) return
    if (typeof window === 'undefined') return
    const Ctx: typeof AudioContext | undefined =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!Ctx) return

    let ctx: AudioContext
    try {
      ctx = new Ctx()
    } catch {
      return
    }
    let source: MediaElementAudioSourceNode
    try {
      source = ctx.createMediaElementSource(v)
    } catch {
      // Element already wired into another graph (HMR re-mount, etc) — bail
      // rather than throw and break the whole recording surface.
      void ctx.close().catch(() => undefined)
      return
    }
    const gain = ctx.createGain()
    gain.gain.value = 0 // start muted; the level effect below will set it
    source.connect(gain)
    gain.connect(ctx.destination)

    ctxRef.current = ctx
    sourceRef.current = source
    gainRef.current = gain

    return () => {
      try {
        source.disconnect()
        gain.disconnect()
      } catch {
        /* node already torn down */
      }
      void ctx.close().catch(() => undefined)
      ctxRef.current = null
      sourceRef.current = null
      gainRef.current = null
    }
  }, [videoRef])

  // React to fader / mute / record-mute changes. Uses setTargetAtTime for a
  // ~10 ms ramp so changes are click-free.
  useEffect(() => {
    const gain = gainRef.current
    const ctx = ctxRef.current
    if (!gain || !ctx) return
    const target = muted || muteForRecord ? 0 : dbToGain(volumeDb)
    try {
      gain.gain.setTargetAtTime(target, ctx.currentTime, 0.01)
    } catch {
      // setTargetAtTime can throw on closed context; fall back to direct set.
      gain.gain.value = target
    }
  }, [volumeDb, muted, muteForRecord])
}
